import React from 'react'
import {
  Typography,
  Stack,
  TextField,
  Button,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Box,
} from '@mui/material'
import CardMembershipIcon from '@mui/icons-material/CardMembership'
import type { LicenseFormState } from '../hooks'
import CollapsibleSection from './CollapsibleSection'

interface LicenseInfoFormProps {
  form: LicenseFormState
  onFieldChange: (field: string, value: string) => void
  onUpdate: () => void
  onResetForm: () => void
  formHasChanges: boolean
  actionLoading: boolean
}

export default function LicenseInfoForm({
  form,
  onFieldChange,
  onUpdate,
  onResetForm,
  formHasChanges,
  actionLoading,
}: LicenseInfoFormProps) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant='h5'
        sx={{
          fontWeight: 600,
          mb: 2,
          borderLeft: '4px solid',
          borderColor: 'primary.main',
          pl: 2,
          py: 0.5,
        }}
      >
        License Information
      </Typography>
      <CollapsibleSection
        title='License Details'
        defaultExpanded
        icon={CardMembershipIcon}
      >
        <Box sx={{ pt: 1, pb: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ mb: 0.5, display: 'block' }}
              >
                Type
              </Typography>
              <FormControl size='small' fullWidth>
                <Select
                  value={form.type}
                  onChange={(e) => onFieldChange('type', e.target.value)}
                  displayEmpty
                >
                  <MenuItem value='district'>District</MenuItem>
                  <MenuItem value='school'>School</MenuItem>
                  <MenuItem value='classroom'>Teacher</MenuItem>
                  <MenuItem value='homeschool'>Family</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ mb: 0.5, display: 'block' }}
              >
                Contact Name
              </Typography>
              <TextField
                size='small'
                value={form.contactName}
                onChange={(e) => onFieldChange('contactName', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ mb: 0.5, display: 'block' }}
              >
                License Name
              </Typography>
              <TextField
                size='small'
                value={form.name}
                onChange={(e) => onFieldChange('name', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ mb: 0.5, display: 'block' }}
              >
                Contact Email
              </Typography>
              <TextField
                size='small'
                value={form.contactEmail}
                onChange={(e) => onFieldChange('contactEmail', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ mb: 0.5, display: 'block' }}
              >
                Start Date
              </Typography>
              <TextField
                size='small'
                type='date'
                value={form.startsAt}
                onChange={(e) => onFieldChange('startsAt', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ mb: 0.5, display: 'block' }}
              >
                End Date
              </Typography>
              <TextField
                size='small'
                type='date'
                value={form.endsAt}
                onChange={(e) => onFieldChange('endsAt', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ mb: 0.5, display: 'block' }}
              >
                District Name (Optional)
              </Typography>
              <TextField
                size='small'
                value={form.districtName}
                onChange={(e) => onFieldChange('districtName', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ mb: 0.5, display: 'block' }}
              >
                School Name (Optional)
              </Typography>
              <TextField
                size='small'
                value={form.schoolName}
                onChange={(e) => onFieldChange('schoolName', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ mb: 0.5, display: 'block' }}
              >
                Seat Limit (Optional)
              </Typography>
              <TextField
                size='small'
                type='number'
                value={form.seatLimit}
                onChange={(e) => onFieldChange('seatLimit', e.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>
          <Stack
            direction='row'
            spacing={2}
            justifyContent='flex-end'
            sx={{ mt: 3 }}
          >
            <Button
              size='large'
              variant='outlined'
              onClick={onResetForm}
              disabled={actionLoading || !formHasChanges}
            >
              Reset form
            </Button>
            <Button
              size='large'
              variant='contained'
              onClick={onUpdate}
              disabled={actionLoading}
            >
              Update License
            </Button>
          </Stack>
        </Box>
      </CollapsibleSection>
    </Box>
  )
}
